/**
 * Storage Availability Detection
 * Story 7.2 - Task 4: Storage fallback for third-party blocking
 *
 * Detects if IndexedDB storage is accessible in iframe context
 * Safari ITP and Firefox ETP block third-party storage in iframes by default
 */

/**
 * Tests if IndexedDB storage is accessible
 * @returns Promise resolving to true if storage is available, false if blocked
 */
export async function isStorageAccessible(): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      // Try to open a test database
      const request = window.indexedDB.open('storage-access-test', 1);

      request.onsuccess = () => {
        // Successfully opened - storage is accessible
        const db = request.result;
        db.close();
        // Clean up test database
        window.indexedDB.deleteDatabase('storage-access-test');
        resolve(true);
      };

      request.onerror = () => {
        console.warn('Storage blocked (Safari ITP, Firefox ETP, or third-party restriction):', request.error);
        resolve(false);
      };

      request.onblocked = () => {
        console.warn('Storage access blocked by browser');
        resolve(false);
      };
    } catch (error) {
      console.warn('Storage blocked (Safari ITP, Firefox ETP, or third-party restriction):', error);
      resolve(false);
    }
  });
}

/**
 * Initializes storage with fallback detection
 * Call this before attempting to use IndexedDB
 *
 * @returns Promise resolving to storage availability status
 */
export async function initStorageWithFallback(): Promise<{
  available: boolean;
  reason?: string;
}> {
  const available = await isStorageAccessible();

  if (!available) {
    return {
      available: false,
      reason: 'Third-party storage blocked - running in API-only mode without caching',
    };
  }

  return { available: true };
}
